import { PhoneVerifyBanner } from "@/components/PhoneVerifyBanner";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/context/AuthContext";
import { Group } from "@/types/group";
import { Invitation } from "@/types/invitation";
import Ionicons from "@expo/vector-icons/Ionicons";
import firestore from "@react-native-firebase/firestore";
import { useRoute } from "@react-navigation/native";
import { Image } from "expo-image";
import { Link, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { getGroupPictureThumbnail } from "../../../utils/getGroupPictureThumbnail";
import { useGroupImageOverride } from "../../../utils/groupImageOverrides";
import { timestampToMillis } from "../../../utils/timestampToMillis";
const DefaultGroupImage = require("../../../assets/images/default-group.png");

// Shape rendered by the groups FlatList. Member groups are full `Group`
// docs; pending entries only carry id+name (from the access request doc)
// plus the `pending` flag — the renderer reads `description` /
// `photoUpdatedAt` only when present and falls back to the default group
// avatar otherwise.
type GroupListItem = {
  id: string;
  name: string;
  description?: string;
  photoUpdatedAt?: Group["photoUpdatedAt"];
  pending: boolean;
  hasUnread: boolean;
};

function GroupAvatar({
  item,
  fallbackOverride,
}: {
  item: GroupListItem;
  fallbackOverride?: string;
}) {
  const override = useGroupImageOverride(item.id);
  const uri =
    override ??
    fallbackOverride ??
    getGroupPictureThumbnail(item.id, item.photoUpdatedAt?.toMillis?.());
  return (
    <Image
      source={{ uri }}
      style={styles.avatar}
      placeholder={DefaultGroupImage}
      cachePolicy="disk"
      transition={300}
    />
  );
}

export default function GroupsScreen() {
  const { user, userData } = useAuth();
  const lastViewedMap = userData?.groupsLastViewedAt;
  const router = useRouter();
  const route = useRoute();
  const params = (route.params || {}) as {
    newGroupImage?: string;
    newGroupId?: string;
  };
  const [groups, setGroups] = useState<Group[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [pendingRequests, setPendingRequests] = useState<
    { id: string; groupId: string; groupName: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (!user) return;

    const groupsQuery = firestore()
      .collection("groups")
      .where("memberIds", "array-contains", user.uid);
    const unsubscribeGroups = groupsQuery.onSnapshot(
      (snapshot) => {
        let groupsData = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Group,
        );
        console.log(params.newGroupImage, params.newGroupId);
        if (params.newGroupImage && params.newGroupId) {
          groupsData = groupsData.map((group) =>
            group.id === params.newGroupId
              ? { ...group, imageUrl: params.newGroupImage }
              : group,
          );
        }
        setGroups(groupsData);
        setLoading(false);
      },
      (error) => {
        const errorCode = (error as { code?: string })?.code;
        if (errorCode === "permission-denied") {
          console.warn("Groups listener permission denied by Firestore rules.");
        } else {
          console.error("Groups listener error:", error);
        }
        setGroups([]);
        setLoading(false);
      },
    );

    // Invitations are keyed on inviteeUid so phone-only users (email === null)
    // receive them too.
    const invitationsQuery = firestore()
      .collection("groupInvitations")
      .where("inviteeUid", "==", user.uid)
      .where("status", "==", "pending");
    const unsubscribeInvites = invitationsQuery.onSnapshot(
      (snapshot) => {
        const invitesData = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Invitation,
        );
        setInvitations(invitesData);
      },
      (error) => {
        const errorCode = (error as { code?: string })?.code;
        if (errorCode === "permission-denied") {
          console.warn(
            "Group invitations listener permission denied by Firestore rules.",
          );
        } else {
          console.error("Group invitations listener error:", error);
        }
        setInvitations([]);
      },
    );

    // Pending join requests this user has filed. Surfaces both the new
    // signup-URL `groupId` flow and the pre-existing manual "Request
    // access" click on a group URL. Same query shape as
    // hooks/useGroupAccess.web.ts lines 64-69. The access request doc
    // already carries groupId + groupName, so no extra reads against the
    // groups collection are needed for the list view.
    const pendingRequestsQuery = firestore()
      .collection("groupAccessRequests")
      .where("requesterId", "==", user.uid)
      .where("status", "==", "pending");
    const unsubscribePendingRequests = pendingRequestsQuery.onSnapshot(
      (snapshot) => {
        setPendingRequests(
          snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              groupId: data.groupId as string,
              groupName: (data.groupName as string) ?? "",
            };
          }),
        );
      },
      (error) => {
        const errorCode = (error as { code?: string })?.code;
        if (errorCode === "permission-denied") {
          console.warn(
            "Pending requests listener permission denied by Firestore rules.",
          );
        } else {
          console.error("Pending requests listener error:", error);
        }
        setPendingRequests([]);
      },
    );

    return () => {
      unsubscribeGroups();
      unsubscribeInvites();
      unsubscribePendingRequests();
    };
  }, [user, params.newGroupId, params.newGroupImage]);

  // Combine member groups with pending join requests for the list view.
  // Dedupe: if a groupId appears in both (stale request that wasn't cleared
  // after approval, or a race between the two snapshots), prefer the
  // member render — the user IS in the group, the pending badge would be
  // misleading.
  const displayItems = useMemo<GroupListItem[]>(() => {
    const memberSet = new Set(groups.map((g) => g.id));
    const pendingItems: GroupListItem[] = pendingRequests
      .filter((p) => !memberSet.has(p.groupId))
      .map((p) => ({
        id: p.groupId,
        name: p.groupName,
        pending: true,
        hasUnread: false,
      }));
    // Sort by lastPostAt desc so groups with the most recent activity float
    // to the top. Groups without lastPostAt sort to the bottom (missing
    // → 0). Array.prototype.sort is stable, so ties keep Firestore's order.
    const sortedGroups = [...groups].sort(
      (a, b) => timestampToMillis(b.lastPostAt) - timestampToMillis(a.lastPostAt),
    );
    const memberItems: GroupListItem[] = sortedGroups.map((g) => {
      const lastPostMs = timestampToMillis(g.lastPostAt);
      const lastViewedMs = timestampToMillis(lastViewedMap?.[g.id]);
      return {
        id: g.id,
        name: g.name,
        description: g.description,
        photoUpdatedAt: g.photoUpdatedAt,
        pending: false,
        hasUnread: lastPostMs > 0 && lastPostMs > lastViewedMs,
      };
    });
    return [...memberItems, ...pendingItems];
  }, [groups, pendingRequests, lastViewedMap]);

  const handleAccept = async (invitationId: string, groupId: string) => {
    if (!user) return;
    const userDocRef = firestore().collection("users").doc(user.uid);
    const groupDocRef = firestore().collection("groups").doc(groupId);
    const invitationDocRef = firestore()
      .collection("groupInvitations")
      .doc(invitationId);

    await userDocRef.update({
      groupIds: firestore.FieldValue.arrayUnion(groupId),
    });
    await groupDocRef.update({
      memberIds: firestore.FieldValue.arrayUnion(user.uid),
    });
    await invitationDocRef.update({ status: "accepted" });

    setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    const groupSnap = await groupDocRef.get();
    if (groupSnap.exists()) {
      const joinedGroup = { id: groupSnap.id, ...groupSnap.data() } as Group;
      setGroups((prev) =>
        prev.some((g) => g.id === joinedGroup.id) ? prev : [...prev, joinedGroup],
      );
    }
  };

  const handleDecline = async (invitationId: string) => {
    const invitationDocRef = firestore()
      .collection("groupInvitations")
      .doc(invitationId);
    await invitationDocRef.update({ status: "declined" });
  };

  return (
    <ThemedView style={styles.container}>
      <PhoneVerifyBanner />
      {invitations.length > 0 && (
        <ThemedView style={styles.invitationsContainer}>
          <ThemedText type="subtitle">{t("INVITATIONS")}</ThemedText>
          <FlatList
            data={invitations}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <ThemedView style={styles.invitationItem}>
                <ThemedText>
                  {t("JOIN")}{" "}
                  <ThemedText type="defaultSemiBold">
                    {item.groupName || "a group"}
                  </ThemedText>
                </ThemedText>
                <ThemedView style={styles.invitationActions}>
                  <Button
                    text={t("ACCEPT")}
                    onPress={() => handleAccept(item.id, item.groupId)}
                  />
                  <Button
                    text={t("DECLINE")}
                    onPress={() => handleDecline(item.id)}
                    style={{ marginLeft: 10, backgroundColor: "red" }}
                  />
                </ThemedView>
              </ThemedView>
            )}
          />
        </ThemedView>
      )}
      {loading ? (
        <ThemedView style={styles.centered}>
          <ActivityIndicator />
        </ThemedView>
      ) : (
        <FlatList
          data={displayItems}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <Link href={`/groups/${item.id}`} asChild>
              <TouchableOpacity style={styles.groupItem}>
                <GroupAvatar
                  item={item}
                  fallbackOverride={
                    params.newGroupImage && params.newGroupId === item.id
                      ? (params.newGroupImage as string)
                      : undefined
                  }
                />
                <ThemedView style={styles.groupItemTextContainer}>
                  <ThemedText type="subtitle">{item.name}</ThemedText>
                  {item.pending ? (
                    <ThemedText style={styles.pendingBadge}>
                      {t("REQUEST_UNDER_REVIEW")}
                    </ThemedText>
                  ) : (
                    <ThemedText>{item.description}</ThemedText>
                  )}
                </ThemedView>
                {item.hasUnread && !item.pending ? (
                  <View
                    style={styles.unreadDot}
                    testID={`group-unread-dot-${item.id}`}
                  />
                ) : null}
              </TouchableOpacity>
            </Link>
          )}
          ListEmptyComponent={() => (
            <ThemedView style={styles.centered}>
              <ThemedText>{t("NO_GROUPS_FOUND")}</ThemedText>
              <ThemedText>{t("CREATE_A_NEW_ONE")}</ThemedText>
            </ThemedView>
          )}
        />
      )}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => router.push("/(tabs)/groups/create")}
      >
        <Ionicons name="add" size={30} color="white" />
      </TouchableOpacity>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  groupItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    flexDirection: "row",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  fab: {
    position: "absolute",
    width: 56,
    height: 56,
    alignItems: "center",
    justifyContent: "center",
    right: 20,
    bottom: 10,
    backgroundColor: "#1e3a8a",
    borderRadius: 30,
    elevation: 8,
    zIndex: 10,
  },
  invitationsContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  invitationItem: {
    paddingVertical: 10,
  },
  invitationActions: {
    flexDirection: "row",
    marginTop: 10,
  },
  pendingBadge: {
    fontSize: 13,
    color: "#1e3a8a",
    fontStyle: "italic",
    marginTop: 2,
  },
  groupItemTextContainer: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#3b82f6",
    alignSelf: "center",
    marginLeft: 8,
  },
});
